import { ref, onMounted, onBeforeUnmount } from 'vue';
import { api } from '@/utils/api';
import { listPendingQueue, markSynced, bumpAttempt } from '@/utils/db';

/**
 * Cola offline genérica.
 *
 * - Lee periódicamente IndexedDB y trata de pushear cada item al backend.
 * - Reintenta con backoff exponencial sencillo (limitado a 5 intentos).
 * - Expone contadores reactivos `pending` y `syncing` para mostrar banner global.
 *
 * Se monta en `App.vue` y queda viva durante toda la sesión.
 */

const POLL_MS = 4_000;
const MAX_ATTEMPTS = 5;

export function useOfflineQueue() {
  const pending = ref(0);
  const syncing = ref(false);
  let timer: ReturnType<typeof setInterval> | null = null;

  async function refresh(): Promise<void> {
    const items = await listPendingQueue();
    pending.value = items.length;
  }

  async function flush(): Promise<void> {
    if (syncing.value) return;
    if (!navigator.onLine) {
      await refresh();
      return;
    }
    syncing.value = true;
    try {
      const items = await listPendingQueue();
      for (const item of items) {
        if (item.attempts >= MAX_ATTEMPTS) continue; // se inspecciona manualmente
        try {
          if (item.kind === 'pickeo_event') {
            await api.post('/v1/pickeo-events', item.payload);
          } else if (item.kind === 'fulfillment') {
            await api.post('/v1/fulfillments', item.payload);
          } else if (item.kind === 'order_status') {
            await api.patch('/v1/orders/status', item.payload);
          }
          await markSynced(item.id);
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'sync error';
          await bumpAttempt(item.id, msg);
          // si falló por red, paramos acá; si fue 4xx, seguimos con el resto.
          if (!navigator.onLine) break;
        }
      }
    } finally {
      syncing.value = false;
      await refresh();
    }
  }

  onMounted(() => {
    void refresh();
    timer = setInterval(() => void flush(), POLL_MS);
  });

  onBeforeUnmount(() => {
    if (timer) clearInterval(timer);
  });

  return { pending, syncing, flush, refresh };
}
